import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KpiCard } from './KpiCard';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { LogIn, UserPlus, Clock, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UsageMetrics } from '@/hooks/useExecutiveUsageMetrics';
import { formatBucketLabel, Granularity } from '@/lib/timeSeries';

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--info))', 'hsl(var(--success))', 'hsl(var(--warning))'];
const DOW = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

interface Props {
  data?: UsageMetrics;
  isLoading: boolean;
  granularity: Granularity;
}

export function PlatformUsageTab({ data, isLoading, granularity }: Props) {
  if (isLoading || !data) {
    return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }

  const p = data.platform;
  const buckets = Array.from(new Set([...p.logins_series.map(s => s.bucket), ...p.signups_series.map(s => s.bucket)])).sort();
  const series = buckets.map(b => ({
    label: formatBucketLabel(b, granularity),
    ingresos: p.logins_series.find(s => s.bucket === b)?.ingresos ?? 0,
    usuarios: p.logins_series.find(s => s.bucket === b)?.usuarios ?? 0,
    registros: p.signups_series.find(s => s.bucket === b)?.registros ?? 0,
  }));

  const hours = Array.from({ length: 24 }, (_, h) => ({
    hora: `${String(h).padStart(2, '0')}h`,
    total: p.by_hour.find(x => x.hora === h)?.total ?? 0,
  }));
  const dows = Array.from({ length: 7 }, (_, d) => ({
    dia: DOW[d],
    total: p.by_dow.find(x => x.dia === d)?.total ?? 0,
  }));

  const maxCell = Math.max(1, ...p.matrix.map(m => m.total));
  const cell = (d: number, h: number) => p.matrix.find(m => m.dia === d && m.hora === h)?.total ?? 0;

  const avgLogins = p.totals.usuarios_login > 0 ? (p.totals.logins / p.totals.usuarios_login).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Inicios de sesión" value={p.totals.logins.toLocaleString('es-CO')} hint={`${p.totals.usuarios_login} usuarios distintos`} icon={<LogIn className="w-4 h-4" />} accent="primary" />
        <KpiCard label="Nuevos registros" value={p.totals.registros.toLocaleString('es-CO')} icon={<UserPlus className="w-4 h-4" />} accent="success" />
        <KpiCard label="Ingresos por usuario" value={avgLogins} hint="Promedio en el periodo" icon={<Zap className="w-4 h-4" />} accent="accent" />
        <KpiCard label="Eventos registrados" value={p.totals.eventos.toLocaleString('es-CO')} icon={<Clock className="w-4 h-4" />} accent="info" />
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Inicios de sesión y registros</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="ingresos" name="Inicios de sesión" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="usuarios" name="Usuarios únicos" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="registros" name="Registros" stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Mapa de actividad (hora × día, hora Colombia)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="flex gap-1 pl-10 mb-1">
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="flex-1 text-[9px] text-center text-muted-foreground">{h}</div>
              ))}
            </div>
            {Array.from({ length: 7 }, (_, d) => (
              <div key={d} className="flex gap-1 items-center mb-1">
                <div className="w-10 text-[11px] text-muted-foreground">{DOW[d]}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = cell(d, h);
                  const opacity = v === 0 ? 0.06 : 0.15 + 0.85 * (v / maxCell);
                  return (
                    <div
                      key={h}
                      title={`${DOW[d]} ${h}:00 · ${v} eventos`}
                      className={cn('flex-1 h-6 rounded-sm bg-primary')}
                      style={{ opacity }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Actividad por hora del día</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={hours}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="hora" fontSize={9} stroke="hsl(var(--muted-foreground))" interval={1} />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="total" name="Eventos" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Actividad por día de la semana</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dows}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="dia" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="total" name="Eventos" fill="hsl(var(--accent))" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Tipos de acción más frecuentes</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={p.actions} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="action" width={150} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="total" name="Eventos" fill="hsl(var(--secondary))" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Frecuencia de ingreso por usuario</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={p.frequency} dataKey="usuarios" nameKey="rango" outerRadius={100} label>
                  {p.frequency.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Usuarios sin ingresar hace más de 30 días</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Organización</TableHead>
                <TableHead>Último ingreso</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {p.inactive.map((u, i) => (
                <TableRow key={`${u.email}-${i}`}>
                  <TableCell>{u.full_name ?? '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.email ?? '—'}</TableCell>
                  <TableCell className="text-xs">{u.org ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('es-CO') : 'Nunca'}
                  </TableCell>
                </TableRow>
              ))}
              {p.inactive.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Sin usuarios inactivos.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
